/* =============================================================================
 * build-a-box.js — Lógica de la plantilla "Arma tu caja"
 * -----------------------------------------------------------------------------
 * JS vanilla, sin dependencias externas. Usa fetch contra el AJAX Cart API
 * nativo de Shopify (/cart/add.js) — la misma ruta que ya usa el theme — por lo
 * que no depende de que jQuery esté cargado antes que este script.
 *
 * MODELO DE PRECIO (precio por pieza según la variante):
 * Cada pieza cuesta el precio de la VARIANTE BASE seleccionada
 * (15→$1050/pieza, 30→$1800/pieza, 60→$3240/pieza). El total es:
 *   precio de la variante × nº de piezas elegidas.
 * Al pagar se agrega la variante base con QUANTITY = nº de piezas (no 1), de
 * modo que Shopify multiplique precio × cantidad. Sigue siendo UNA sola línea
 * (un paquete); los productos elegidos se guardan como line item properties.
 *
 * ⚠️  LIMITACIÓN DE INVENTARIO (IMPORTANTE):
 * Este modelo NO descuenta el stock de cada producto componente: solo descuenta
 * la variante base. Los productos elegidos van como propiedades (texto legible
 * + JSON "_seleccion") para referencia/fulfillment manual. Si necesitas
 * descuento de stock real por componente, se requiere Bundles nativos de
 * Shopify o una app de bundles (fuera del alcance de esta plantilla).
 * =============================================================================
 */

(function () {
  'use strict';

  /**
   * Inicializa una instancia de "Arma tu caja" sobre su contenedor raíz.
   * Soporta múltiples instancias por página (cada sección es independiente).
   */
  function BuildABox(root) {
    this.root = root;

    // ---- Config desde data-attributes ----
    this.cartAddUrl = root.dataset.cartAddUrl || '/cart/add.js';
    this.allowRepeat = root.dataset.allowRepeat === 'true';
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    try {
      this.strings = JSON.parse(root.dataset.strings || '{}');
    } catch (e) {
      this.strings = {};
    }

    // ---- Referencias al DOM ----
    this.variantBtns = Array.prototype.slice.call(root.querySelectorAll('[data-variant-btn]'));
    this.progressWrap = root.querySelector('[data-progress-wrap]');
    this.progressFill = root.querySelector('[data-progress-fill]');
    this.progressBar = root.querySelector('.build-a-box__progress-bar');
    this.countCurrentEl = root.querySelector('[data-count-current]');
    this.countTargetEl = root.querySelector('[data-count-target]');
    this.gridWrap = root.querySelector('[data-grid-wrap]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-card]'));
    this.footer = root.querySelector('[data-footer]');
    this.hint = root.querySelector('[data-hint]');
    this.payBtn = root.querySelector('[data-pay]');
    this.payLabel = root.querySelector('[data-pay-label]');
    this.spinner = root.querySelector('[data-spinner]');
    this.totalEl = root.querySelector('[data-total]');

    // ---- Estado ----
    this.baseVariantId = null;   // id de la variante base elegida (15/30/60)
    this.baseVariantPrice = 0;   // precio fijo de la caja (centavos)
    this.target = 0;             // objetivo de piezas según la variante
    this.total = 0;             // suma actual de piezas seleccionadas
    this.totalPrice = 0;         // suma actual de precios (centavos)
    // selección: { [variantId]: { qty, title, cardEl } }
    this.selection = {};
    this.isSubmitting = false;

    this.bindEvents();
  }

  /**
   * Extrae el número objetivo (15/30/60...) del título de la variante.
   * Funciona con "15", "15 piezas", "Caja 30 pcs", etc. Toma el primer entero.
   */
  BuildABox.prototype.parseTarget = function (title) {
    var match = String(title || '').match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  BuildABox.prototype.bindEvents = function () {
    var self = this;

    // --- Selección de variante (paso 1) ---
    this.variantBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        self.selectVariant(btn);
      });
    });

    // --- Botones +/- de cada tarjeta (delegación) ---
    this.cards.forEach(function (card) {
      var plus = card.querySelector('[data-plus]');
      var minus = card.querySelector('[data-minus]');
      if (plus) plus.addEventListener('click', function () { self.changeQty(card, +1); });
      if (minus) minus.addEventListener('click', function () { self.changeQty(card, -1); });
    });

    // --- Botón pagar ---
    if (this.payBtn) {
      this.payBtn.addEventListener('click', function () { self.checkout(); });
    }
  };

  /**
   * Selecciona una variante base. Resetea el contador y el grid (requisito:
   * al cambiar de variante se reinicia todo).
   */
  BuildABox.prototype.selectVariant = function (btn) {
    // Estado visual de los radios
    this.variantBtns.forEach(function (b) {
      b.classList.remove('is-selected');
      b.setAttribute('aria-checked', 'false');
    });
    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');

    this.baseVariantId = btn.dataset.variantId;
    this.baseVariantTitle = btn.dataset.variantTitle || '';
    // Precio FIJO de la caja = precio de la variante base seleccionada (centavos).
    this.baseVariantPrice = parseInt(btn.dataset.variantPrice, 10) || 0;
    this.target = this.parseTarget(btn.dataset.variantTitle);

    // Reset total + selección + contadores visibles de cada tarjeta
    this.resetSelection();

    // Mostrar progreso, grid y footer
    if (this.progressWrap) this.progressWrap.hidden = false;
    if (this.gridWrap) this.gridWrap.hidden = false;
    if (this.footer) this.footer.hidden = false;

    if (this.countTargetEl) this.countTargetEl.textContent = this.target;
    if (this.progressBar) this.progressBar.setAttribute('aria-valuemax', this.target);

    this.render();
  };

  /** Reinicia la selección y los contadores de todas las tarjetas. */
  BuildABox.prototype.resetSelection = function () {
    this.selection = {};
    this.total = 0;
    this.totalPrice = 0;
    this.cards.forEach(function (card) {
      var qtyEl = card.querySelector('[data-qty]');
      if (qtyEl) qtyEl.textContent = '0';
      card.classList.remove('is-active');
    });
  };

  /**
   * Suma o resta unidades de un producto del grid.
   * @param {HTMLElement} card
   * @param {number} delta +1 / -1
   */
  BuildABox.prototype.changeQty = function (card, delta) {
    // Validaciones de guarda
    if (!this.baseVariantId) return;                      // sin variante elegida
    if (card.dataset.available !== 'true') return;        // agotado
    if (delta > 0 && this.total >= this.target) return;   // no exceder objetivo

    var variantId = card.dataset.variantId;
    var entry = this.selection[variantId];
    var currentQty = entry ? entry.qty : 0;

    // Si no se permite repetir, el máximo por producto es 1
    if (delta > 0 && !this.allowRepeat && currentQty >= 1) return;

    var newQty = currentQty + delta;
    if (newQty < 0) return;

    if (newQty === 0) {
      delete this.selection[variantId];
    } else {
      this.selection[variantId] = {
        qty: newQty,
        title: card.dataset.productTitle || ''
      };
    }

    // Recalcular total (piezas) desde cero (fuente de verdad = selection)
    this.recalcTotal();

    // Actualizar contador visible de esta tarjeta
    var qtyEl = card.querySelector('[data-qty]');
    if (qtyEl) qtyEl.textContent = String(newQty);
    card.classList.toggle('is-active', newQty > 0);

    this.render();
  };

  BuildABox.prototype.recalcTotal = function () {
    var sum = 0;        // piezas
    for (var id in this.selection) {
      if (Object.prototype.hasOwnProperty.call(this.selection, id)) {
        sum += this.selection[id].qty;
      }
    }
    this.total = sum;
    // Cada pieza cuesta el precio de la variante base seleccionada.
    // Total = precio de la variante × nº de piezas elegidas.
    this.totalPrice = (this.baseVariantPrice || 0) * sum;
  };

  /**
   * Formatea centavos al formato de moneda de la tienda. Reutiliza el formateador
   * del theme si existe (para respetar moneda/decimales); si no, hace un fallback
   * compatible con los patrones estándar de Shopify ({{amount}}, etc.).
   */
  BuildABox.prototype.formatMoney = function (cents) {
    cents = cents || 0;
    // 1) Usar el formateador del theme si está disponible.
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, this.moneyFormat);
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, this.moneyFormat);
    }
    // 2) Fallback mínimo (soporta {{amount}} y {{amount_no_decimals}}).
    var format = this.moneyFormat || '${{amount}}';
    var value = (cents / 100);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, token) {
      if (token === 'amount_no_decimals') return String(Math.round(value));
      // Miles con coma, 2 decimales con punto.
      return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    });
  };

  /**
   * Repinta progreso, estado de botones + y del botón PAGAR.
   * El + se deshabilita al alcanzar el objetivo; PAGAR solo si total == target.
   */
  BuildABox.prototype.render = function () {
    // --- Total en vivo (suma de los productos elegidos) ---
    if (this.totalEl) this.totalEl.textContent = this.formatMoney(this.totalPrice);

    // --- Progreso ---
    if (this.countCurrentEl) this.countCurrentEl.textContent = this.total;
    if (this.progressFill) {
      var pct = this.target > 0 ? Math.min(100, (this.total / this.target) * 100) : 0;
      this.progressFill.style.width = pct + '%';
    }
    if (this.progressBar) this.progressBar.setAttribute('aria-valuenow', this.total);

    var atLimit = this.total >= this.target;

    // --- Botones + de cada tarjeta ---
    var self = this;
    this.cards.forEach(function (card) {
      var plus = card.querySelector('[data-plus]');
      if (!plus) return;
      if (card.dataset.available !== 'true') { plus.disabled = true; return; }

      var entry = self.selection[card.dataset.variantId];
      var qty = entry ? entry.qty : 0;
      // Deshabilitar + si: llegamos al límite global, o no se permite repetir y ya hay 1
      var blockRepeat = !self.allowRepeat && qty >= 1;
      plus.disabled = atLimit || blockRepeat;
    });

    // --- Botón pagar + mensaje de ayuda ---
    var exact = this.target > 0 && this.total === this.target;
    if (this.payBtn) {
      this.payBtn.disabled = !exact || this.isSubmitting;
      this.payBtn.setAttribute('aria-disabled', String(!exact || this.isSubmitting));
      this.payBtn.classList.toggle('is-ready', exact);
    }
    if (this.hint) {
      if (exact) {
        this.hint.textContent = '';
      } else {
        var remaining = this.target - this.total;
        var pieces = this.strings.pieces || 'piezas';
        this.hint.textContent = remaining > 0
          ? ('Faltan ' + remaining + ' ' + pieces)
          : '';
      }
    }
  };

  /**
   * Agrega al carrito la VARIANTE BASE seleccionada (1 unidad), cuyo precio fijo
   * define el total de la caja (15→$1050, 30→$1800, 60→$3240). Los productos
   * elegidos se guardan como line item properties. Luego redirige a /checkout.
   */
  BuildABox.prototype.checkout = function () {
    if (this.isSubmitting) return;
    if (!this.baseVariantId || this.total !== this.target) return;

    this.isSubmitting = true;
    this.setLoading(true);

    var payload = {
      items: [
        {
          id: parseInt(this.baseVariantId, 10),
          // Cantidad = nº de piezas: así el precio de la variante se cobra por
          // cada pieza (precio variante × piezas = total). Sigue siendo 1 línea.
          quantity: this.total,
          properties: this.buildProperties()
        }
      ]
    };

    var self = this;
    fetch(this.cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (!res.ok) { return res.json().then(function (j) { throw j; }); }
        return res.json();
      })
      .then(function () {
        // Éxito: ir directo a checkout
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        self.isSubmitting = false;
        self.setLoading(false);
        self.render();
        // Shopify devuelve el detalle en `description` (un item) o `errors` (varios/stock).
        var msg = (err && (err.description || err.errors)) || self.strings.addError ||
                  'Error al agregar al carrito.';
        if (self.hint) self.hint.textContent = msg;
        // eslint-disable-next-line no-console
        console.error('[build-a-box] add to cart failed:', err);
      });
  };

  /**
   * Genera las line item properties de la línea base:
   *  - "Pieza N": "Título xCantidad"  (legible para cliente/staff)
   *  - "_seleccion": JSON {variant_id: cantidad}  (oculta, para fulfillment)
   */
  BuildABox.prototype.buildProperties = function () {
    var props = {};
    var seleccion = {};
    var prefix = this.strings.linePieceLabel || 'Pieza';
    var i = 1;

    for (var id in this.selection) {
      if (!Object.prototype.hasOwnProperty.call(this.selection, id)) continue;
      var item = this.selection[id];
      props[prefix + ' ' + i] = item.title + ' x' + item.qty;
      seleccion[id] = item.qty;
      i++;
    }

    // Propiedades que empiezan con "_" quedan ocultas en carrito/checkout.
    props['_seleccion'] = JSON.stringify(seleccion);
    return props;
  };

  /** Muestra/oculta el spinner del botón pagar. */
  BuildABox.prototype.setLoading = function (loading) {
    if (this.spinner) this.spinner.hidden = !loading;
    if (this.payBtn) this.payBtn.disabled = loading;
    if (this.payLabel) this.payLabel.style.opacity = loading ? '0.6' : '1';
  };

  // ---- Bootstrap: inicializa todas las secciones presentes ----
  function init() {
    var roots = document.querySelectorAll('[data-build-a-box]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__buildABoxInit) return; // evitar doble init
      root.__buildABoxInit = true;
      new BuildABox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Reinicialización al recargar la sección en el editor de temas (Shopify)
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-build-a-box]');
    if (root && !root.__buildABoxInit) {
      root.__buildABoxInit = true;
      new BuildABox(root);
    }
  });
})();
